import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { fmtErr } from "../lib/api";
import { Card, Section, TH, TD, Empty, Button, Badge, Label, Input } from "../components/ui";
import { Boxes, KeyRound, Plug, ArrowLeft, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "settings", label: "Impostazioni" },
  { key: "credentials", label: "Credenziali" },
  { key: "taxes", label: "Mappatura Imposte" },
  { key: "series", label: "Mappatura Serie" },
  { key: "accounts", label: "Mappatura Conti" },
];

export default function HoldedDetail() {
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);
  const [tab, setTab] = useState("settings");

  const load = () => api.get(`/companies/${companyId}`).then(({ data }) => setCompany(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId]);

  if (!company) return <div className="text-ink-muted text-sm">Caricamento…</div>;

  return (
    <div className="space-y-6" data-testid="holded-detail-page">
      <div>
        <Link to="/holded" className="text-xs text-ink-muted hover:text-ink inline-flex items-center gap-1">
          <ArrowLeft size={12} /> Sync Holded
        </Link>
        <h1 className="font-display font-bold text-3xl mt-1 flex items-center gap-2">
          <Boxes size={26} /> {company.name}
        </h1>
        <div className="text-xs text-ink-muted font-mono mt-1">
          Zoho org: {company.zoho_organization_id || "—"} · Holded base: {company.holded_base_url}
        </div>
      </div>

      <div className="border-b border-border-default flex gap-4">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
                  data-testid={`tab-${t.key}`}
                  className={`text-sm font-medium pb-2.5 border-b-2 transition-colors ${
                    tab === t.key ? "border-ink text-ink" : "border-transparent text-ink-muted hover:text-ink"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "settings" && <SettingsTab company={company} reload={load} />}
      {tab === "credentials" && <CredentialsTab companyId={companyId} />}
      {tab === "taxes" && <TaxMappingsTab companyId={companyId} />}
      {tab === "series" && <SeriesMappingsTab companyId={companyId} />}
      {tab === "accounts" && <AccountMappingsTab companyId={companyId} />}
    </div>
  );
}

function SettingsTab({ company, reload }) {
  const [f, setF] = useState(company);
  useEffect(() => setF(company), [company]);
  const save = async () => {
    try {
      await api.put(`/holded/companies/${company.id}/settings`, {
        has_holded_target: !!f.has_holded_target,
        holded_base_url: f.holded_base_url || "https://api.holded.com/api/v2",
        holded_default_invoice_series_id: f.holded_default_invoice_series_id || null,
        holded_default_creditnote_series_id: f.holded_default_creditnote_series_id || null,
        holded_default_account_id: f.holded_default_account_id || null,
        holded_auto_approve_invoices: !!f.holded_auto_approve_invoices,
      });
      toast.success("Impostazioni salvate");
      reload();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };
  return (
    <Card className="p-6 grid grid-cols-2 gap-4">
      <div className="col-span-2 flex items-center gap-2">
        <input type="checkbox" id="hh" checked={!!f.has_holded_target}
               onChange={(e) => setF({ ...f, has_holded_target: e.target.checked })}
               data-testid="check-has-holded" />
        <label htmlFor="hh" className="text-sm font-medium">Abilita target Holded per questa società</label>
      </div>
      <div className="col-span-2"><Label>Holded Base URL</Label>
        <Input value={f.holded_base_url || ""}
               onChange={(e) => setF({ ...f, holded_base_url: e.target.value })} /></div>
      <div><Label>Serie default fatture (Holded ID)</Label>
        <Input value={f.holded_default_invoice_series_id || ""}
               onChange={(e) => setF({ ...f, holded_default_invoice_series_id: e.target.value })}
               placeholder="es: 6a283a2bf57bd500be0761f9"
               data-testid="input-default-invoice-series" /></div>
      <div><Label>Serie default note credito (Holded ID)</Label>
        <Input value={f.holded_default_creditnote_series_id || ""}
               onChange={(e) => setF({ ...f, holded_default_creditnote_series_id: e.target.value })} /></div>
      <div><Label>Conto contabile default (Holded ID)</Label>
        <Input value={f.holded_default_account_id || ""}
               onChange={(e) => setF({ ...f, holded_default_account_id: e.target.value })} /></div>
      <div className="flex items-center gap-2 mt-6">
        <input type="checkbox" id="ha" checked={!!f.holded_auto_approve_invoices}
               onChange={(e) => setF({ ...f, holded_auto_approve_invoices: e.target.checked })}
               data-testid="check-auto-approve" />
        <label htmlFor="ha" className="text-sm">Approvazione automatica fatture (sconsigliato finché non validato VeriFactu)</label>
      </div>
      <div className="col-span-2 flex justify-end">
        <Button onClick={save} data-testid="btn-save-settings">Salva impostazioni</Button>
      </div>
    </Card>
  );
}

function CredentialsTab({ companyId }) {
  const [pat, setPat] = useState("");
  const [testResult, setTestResult] = useState(null);
  const save = async () => {
    if (pat.length < 8) { toast.error("PAT troppo corto"); return; }
    try {
      await api.post(`/holded/companies/${companyId}/credentials`, { pat });
      toast.success("PAT salvato (cifrato in DB)");
      setPat("");
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };
  const test = async () => {
    setTestResult(null);
    try {
      const { data } = await api.get(`/holded/companies/${companyId}/test`);
      setTestResult({ ok: true, info: data });
      toast.success("Connessione Holded OK");
    } catch (e) {
      setTestResult({ ok: false, err: fmtErr(e.response?.data?.detail) || e.message });
      toast.error("Connessione Holded fallita");
    }
  };
  return (
    <div className="space-y-4">
      <Card className="p-6 space-y-4">
        <h3 className="font-display font-medium text-lg flex items-center gap-2"><KeyRound size={16} /> Personal Access Token (PAT) Holded</h3>
        <div className="text-xs text-ink-muted">Crea il token su Holded → Configuración → Desarrolladores → API. Il valore viene cifrato in DB con Fernet e non sarà più mostrato.</div>
        <div className="flex gap-2 items-end">
          <div className="flex-1"><Label>Incolla PAT</Label>
            <Input type="password" value={pat} onChange={(e) => setPat(e.target.value)}
                   placeholder="pat_xxxxxxx..." data-testid="input-pat" /></div>
          <Button onClick={save} data-testid="btn-save-pat">Salva PAT</Button>
        </div>
      </Card>
      <Card className="p-6 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-medium text-lg flex items-center gap-2"><Plug size={16} /> Test connessione</h3>
          <Button variant="secondary" onClick={test} data-testid="btn-test-holded">Esegui test</Button>
        </div>
        {testResult && (testResult.ok
          ? <pre className="text-xs bg-surface-hover p-3 rounded font-mono overflow-auto">{JSON.stringify(testResult.info, null, 2)}</pre>
          : <div className="text-sm text-error">{testResult.err}</div>)}
      </Card>
    </div>
  );
}

function TaxMappingsTab({ companyId }) {
  const [rows, setRows] = useState([]);
  const [holdedTaxes, setHoldedTaxes] = useState([]);
  const [d, setD] = useState({ zoho_tax_id: "", zoho_tax_name: "", zoho_tax_percentage: "", holded_tax_id: "", holded_tax_name: "" });
  const load = () => api.get(`/holded/companies/${companyId}/tax-mappings`).then(({ data }) => setRows(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId]);
  const fetchHoldedTaxes = async () => {
    try {
      const { data } = await api.get(`/holded/companies/${companyId}/taxes`);
      setHoldedTaxes(data); toast.success(`${data.length} imposte caricate da Holded`);
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };
  const save = async () => {
    try {
      const ht = holdedTaxes.find((t) => t.id === d.holded_tax_id);
      await api.post(`/holded/companies/${companyId}/tax-mappings`, {
        ...d, holded_tax_name: ht?.name || d.holded_tax_name || null,
        zoho_tax_percentage: d.zoho_tax_percentage ? parseFloat(d.zoho_tax_percentage) : null,
      });
      toast.success("Mapping salvato");
      setD({ zoho_tax_id: "", zoho_tax_name: "", zoho_tax_percentage: "", holded_tax_id: "", holded_tax_name: "" });
      load();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare?")) return;
    await api.delete(`/holded/companies/${companyId}/tax-mappings/${id}`);
    load();
  };
  return (
    <div className="space-y-4">
      <Card className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-medium text-lg">Nuovo mapping imposta</h3>
          <Button variant="secondary" onClick={fetchHoldedTaxes} data-testid="btn-fetch-holded-taxes">
            <RefreshCw size={13} className="mr-1.5" /> Carica imposte da Holded
          </Button>
        </div>
        <div className="grid grid-cols-9 gap-3 items-end">
          <div className="col-span-2"><Label>Zoho Tax ID *</Label>
            <Input value={d.zoho_tax_id} onChange={(e) => setD({ ...d, zoho_tax_id: e.target.value })} data-testid="input-zoho-tax-id" /></div>
          <div className="col-span-2"><Label>Nome Zoho</Label>
            <Input value={d.zoho_tax_name} onChange={(e) => setD({ ...d, zoho_tax_name: e.target.value })} /></div>
          <div><Label>%</Label>
            <Input type="number" step="0.01" value={d.zoho_tax_percentage} onChange={(e) => setD({ ...d, zoho_tax_percentage: e.target.value })} /></div>
          <div className="col-span-3"><Label>Holded Tax</Label>
            <select className="w-full h-9 px-3 rounded-md border border-border-default text-sm bg-surface"
                    value={d.holded_tax_id}
                    onChange={(e) => setD({ ...d, holded_tax_id: e.target.value })}
                    data-testid="select-holded-tax">
              <option value="">— seleziona —</option>
              {holdedTaxes.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.amount}%)</option>)}
            </select></div>
          <div><Button onClick={save} className="w-full" data-testid="btn-save-tax">Salva</Button></div>
        </div>
        {holdedTaxes.length === 0 && <div className="text-xs text-ink-muted">Premi "Carica imposte da Holded" per popolare il selettore.</div>}
      </Card>
      <Card className="overflow-hidden">
        <table className="w-full">
          <thead className="bg-surface-hover/50 border-b border-border-default">
            <tr><TH>Zoho ID</TH><TH>Nome Zoho</TH><TH>%</TH><TH>→</TH><TH>Holded ID</TH><TH>Nome Holded</TH><TH></TH></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-border-divider">
                <TD className="font-mono text-xs">{r.zoho_tax_id}</TD>
                <TD>{r.zoho_tax_name || "—"}</TD>
                <TD className="tabular-nums">{r.zoho_tax_percentage ?? "—"}</TD>
                <TD>→</TD>
                <TD className="font-mono text-xs">{r.holded_tax_id}</TD>
                <TD>{r.holded_tax_name || "—"}</TD>
                <TD><button className="text-xs text-error hover:underline" onClick={() => remove(r.id)}><Trash2 size={12} className="inline" /></button></TD>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7}><Empty>Nessun mapping.</Empty></td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function SeriesMappingsTab({ companyId }) {
  const [rows, setRows] = useState([]);
  const [holdedSeries, setHoldedSeries] = useState({ invoice: [], creditnote: [] });
  const [d, setD] = useState({ doc_type: "invoice", match_mode: "prefix", match_value: "", holded_series_id: "", priority: 0 });
  const load = () => api.get(`/holded/companies/${companyId}/series-mappings`).then(({ data }) => setRows(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId]);
  const fetchHoldedSeries = async () => {
    try {
      const [inv, cn] = await Promise.all([
        api.get(`/holded/companies/${companyId}/series?doc_type=invoice`),
        api.get(`/holded/companies/${companyId}/series?doc_type=creditnote`),
      ]);
      setHoldedSeries({ invoice: inv.data, creditnote: cn.data });
      toast.success(`${inv.data.length + cn.data.length} serie caricate`);
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };
  const save = async () => {
    try {
      const list = holdedSeries[d.doc_type] || [];
      const found = list.find((s) => s.id === d.holded_series_id);
      await api.post(`/holded/companies/${companyId}/series-mappings`, {
        ...d, priority: parseInt(d.priority) || 0,
        match_value: d.match_mode === "default" ? null : (d.match_value || null),
        holded_series_name: found?.name || null,
      });
      toast.success("Regola salvata");
      setD({ doc_type: "invoice", match_mode: "prefix", match_value: "", holded_series_id: "", priority: 0 });
      load();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare?")) return;
    await api.delete(`/holded/companies/${companyId}/series-mappings/${id}`);
    load();
  };
  return (
    <div className="space-y-4">
      <Card className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-medium text-lg">Nuova regola serie</h3>
          <Button variant="secondary" onClick={fetchHoldedSeries} data-testid="btn-fetch-holded-series">
            <RefreshCw size={13} className="mr-1.5" /> Carica serie da Holded
          </Button>
        </div>
        <div className="grid grid-cols-10 gap-3 items-end">
          <div className="col-span-2"><Label>Doc</Label>
            <select className="w-full h-9 px-3 rounded-md border border-border-default text-sm bg-surface"
                    value={d.doc_type} onChange={(e) => setD({ ...d, doc_type: e.target.value, holded_series_id: "" })}>
              <option value="invoice">Fattura</option>
              <option value="creditnote">Nota credito</option>
            </select></div>
          <div className="col-span-2"><Label>Match</Label>
            <select className="w-full h-9 px-3 rounded-md border border-border-default text-sm bg-surface"
                    value={d.match_mode} onChange={(e) => setD({ ...d, match_mode: e.target.value })}>
              <option value="prefix">Prefix</option>
              <option value="regex">Regex</option>
              <option value="default">Default (catch-all)</option>
            </select></div>
          <div className="col-span-2"><Label>Valore match</Label>
            <Input disabled={d.match_mode === "default"} value={d.match_value}
                   onChange={(e) => setD({ ...d, match_value: e.target.value })}
                   placeholder={d.match_mode === "prefix" ? "es: ELX" : d.match_mode === "regex" ? "^F.+" : "—"} /></div>
          <div className="col-span-3"><Label>Holded series</Label>
            <select className="w-full h-9 px-3 rounded-md border border-border-default text-sm bg-surface"
                    value={d.holded_series_id} onChange={(e) => setD({ ...d, holded_series_id: e.target.value })}>
              <option value="">— seleziona —</option>
              {(holdedSeries[d.doc_type] || []).map((s) =>
                <option key={s.id} value={s.id}>{s.name} ({s.format}) {s.verifactu_excluded ? "🚫VF" : ""}</option>)}
            </select></div>
          <div><Label>Pri.</Label>
            <Input type="number" value={d.priority} onChange={(e) => setD({ ...d, priority: e.target.value })} /></div>
        </div>
        <div className="flex justify-end"><Button onClick={save}>Salva regola</Button></div>
      </Card>
      <Card className="overflow-hidden">
        <table className="w-full">
          <thead className="bg-surface-hover/50 border-b border-border-default">
            <tr><TH>Doc</TH><TH>Match</TH><TH>Valore</TH><TH>→</TH><TH>Serie Holded</TH><TH>Pri</TH><TH></TH></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-border-divider">
                <TD>{r.doc_type === "creditnote" ? "Nota cred." : "Fattura"}</TD>
                <TD>{r.match_mode}</TD>
                <TD className="font-mono text-xs">{r.match_value || "—"}</TD>
                <TD>→</TD>
                <TD>{r.holded_series_name || <span className="font-mono text-xs">{r.holded_series_id}</span>}</TD>
                <TD className="tabular-nums">{r.priority}</TD>
                <TD><button className="text-xs text-error hover:underline" onClick={() => remove(r.id)}><Trash2 size={12} className="inline" /></button></TD>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7}><Empty>Nessuna regola.</Empty></td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function AccountMappingsTab({ companyId }) {
  const [rows, setRows] = useState([]);
  const [holdedAccounts, setHoldedAccounts] = useState([]);
  const [d, setD] = useState({ source_type: "zoho_item_id", source_key: "", holded_account_id: "", priority: 0 });
  const load = () => api.get(`/holded/companies/${companyId}/account-mappings`).then(({ data }) => setRows(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId]);
  const fetchAccounts = async () => {
    try {
      const { data } = await api.get(`/holded/companies/${companyId}/accounts`);
      setHoldedAccounts(data); toast.success(`${data.length} conti caricati`);
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };
  const save = async () => {
    try {
      const acc = holdedAccounts.find((a) => a.id === d.holded_account_id);
      await api.post(`/holded/companies/${companyId}/account-mappings`, {
        ...d, priority: parseInt(d.priority) || 0,
        source_key: d.source_type === "default" ? null : (d.source_key || null),
        holded_account_number: acc?.number ? String(acc.number) : null,
        holded_account_name: acc?.name || null,
      });
      toast.success("Regola salvata");
      setD({ source_type: "zoho_item_id", source_key: "", holded_account_id: "", priority: 0 });
      load();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare?")) return;
    await api.delete(`/holded/companies/${companyId}/account-mappings/${id}`);
    load();
  };
  return (
    <div className="space-y-4">
      <Card className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-medium text-lg">Nuova regola conto contabile</h3>
          <Button variant="secondary" onClick={fetchAccounts} data-testid="btn-fetch-holded-accounts">
            <RefreshCw size={13} className="mr-1.5" /> Carica conti da Holded
          </Button>
        </div>
        <div className="grid grid-cols-10 gap-3 items-end">
          <div className="col-span-3"><Label>Origine</Label>
            <select className="w-full h-9 px-3 rounded-md border border-border-default text-sm bg-surface"
                    value={d.source_type} onChange={(e) => setD({ ...d, source_type: e.target.value })}>
              <option value="zoho_item_id">Zoho item_id (preciso)</option>
              <option value="zoho_category">Zoho category</option>
              <option value="zoho_name_prefix">Prefisso nome (es. "Affitto ELX")</option>
              <option value="default">Default (catch-all)</option>
            </select></div>
          <div className="col-span-3"><Label>Valore origine</Label>
            <Input disabled={d.source_type === "default"} value={d.source_key}
                   onChange={(e) => setD({ ...d, source_key: e.target.value })}
                   placeholder={d.source_type === "default" ? "—" : "es: 4253000000123456 oppure 'Affitto'"} /></div>
          <div className="col-span-3"><Label>Conto Holded</Label>
            <select className="w-full h-9 px-3 rounded-md border border-border-default text-sm bg-surface"
                    value={d.holded_account_id} onChange={(e) => setD({ ...d, holded_account_id: e.target.value })}>
              <option value="">— seleziona —</option>
              {holdedAccounts.map((a) =>
                <option key={a.id} value={a.id}>{a.number} — {a.name}</option>)}
            </select></div>
          <div><Label>Pri.</Label>
            <Input type="number" value={d.priority} onChange={(e) => setD({ ...d, priority: e.target.value })} /></div>
        </div>
        <div className="flex justify-end"><Button onClick={save}>Salva regola</Button></div>
      </Card>
      <Card className="overflow-hidden">
        <table className="w-full">
          <thead className="bg-surface-hover/50 border-b border-border-default">
            <tr><TH>Origine</TH><TH>Valore</TH><TH>→</TH><TH>Conto</TH><TH>Pri</TH><TH></TH></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-border-divider">
                <TD>{r.source_type}</TD>
                <TD className="font-mono text-xs">{r.source_key || "—"}</TD>
                <TD>→</TD>
                <TD>{r.holded_account_number ? <span className="font-mono">{r.holded_account_number}</span> : null} {r.holded_account_name || ""}</TD>
                <TD className="tabular-nums">{r.priority}</TD>
                <TD><button className="text-xs text-error hover:underline" onClick={() => remove(r.id)}><Trash2 size={12} className="inline" /></button></TD>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6}><Empty>Nessuna regola. Almeno una regola "Default" è necessaria per il sync.</Empty></td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
